import { useState } from 'react';

import { Box, Button, Card, Flex, Group, NumberInput, Table, Text } from '@mantine/core';

import { DEFAULT_CREDIT_AMOUNT, SubscriptionBillingCycle } from 'models';
import { IntlHelpers } from 'helpers/IntlHelpers';

import { CREDIT_SERVICES } from 'components/subscriptions/subscription-features/data';

export const CREDIT_PACKAGE_AMOUNTS = [DEFAULT_CREDIT_AMOUNT, 5 * DEFAULT_CREDIT_AMOUNT, 20 * DEFAULT_CREDIT_AMOUNT];
export const CUSTOM_VALUE = 'custom';

type CreditPackageSelectorProps = {
    value: number | null;
    onChange: (value: number | null) => void;
    showNone?: boolean;
    disabled?: boolean;
    billingCycle?: SubscriptionBillingCycle;
    PriceTag?: React.ReactNode;
};

export const CreditPackageSelector = ({
    value,
    onChange,
    showNone = false,
    disabled = false,
    billingCycle,
    PriceTag,
}: CreditPackageSelectorProps) => {
    const [isCustomMode, setIsCustomMode] = useState(false);
    const [customValue, setCustomValue] = useState(DEFAULT_CREDIT_AMOUNT);

    const handlePresetClick = (amount: number) => {
        setIsCustomMode(false);
        onChange(amount);
    };

    const handleNoneClick = () => {
        setIsCustomMode(false);
        onChange(null);
    };

    const handleCustomClick = () => {
        setIsCustomMode(true);
        onChange(customValue);
    };

    const handleCustomValueChange = (newValue: number) => {
        setCustomValue(newValue);
        if (isCustomMode) {
            onChange(newValue);
        }
    };

    const isSelected = (amount: number) => !isCustomMode && value === amount;
    const isNoneSelected = !isCustomMode && !value;

    const multiplier = billingCycle === SubscriptionBillingCycle.YEARLY ? 12 : 1;

    return (
        <Card withBorder padding="lg" radius="md" mt={8}>
            <Flex justify="space-between" align="flex-start">
                <Box>
                    <Text fw={600} mb={4}>
                        Credits for simulations and expert support
                    </Text>
                    <Text c="dimmed" size="sm" mb="md">
                        Credits accumulate over time and expire 1 year after purchase.
                    </Text>
                </Box>

                {PriceTag}
            </Flex>

            <Group gap={6}>
                {showNone && (
                    <Button
                        variant={isNoneSelected ? 'filled' : 'default'}
                        onClick={handleNoneClick}
                        disabled={disabled}
                        size="xs"
                    >
                        None
                    </Button>
                )}
                {CREDIT_PACKAGE_AMOUNTS.map((amount) => (
                    <Button
                        key={amount}
                        variant={isSelected(amount) ? 'filled' : 'default'}
                        onClick={() => handlePresetClick(amount)}
                        disabled={disabled}
                        size="xs"
                    >
                        {`${IntlHelpers.formatNumber(amount * multiplier)} credits`}
                    </Button>
                ))}
                <Button
                    variant={isCustomMode ? 'filled' : 'default'}
                    onClick={handleCustomClick}
                    disabled={disabled}
                    size="xs"
                >
                    Custom ({IntlHelpers.formatNumber(customValue * multiplier)} credits)
                </Button>
            </Group>

            {isCustomMode && (
                <Card withBorder bg="gray.0" radius="md" mt="sm" p="md">
                    <Group align="center" gap="sm">
                        <NumberInput
                            style={{ width: 100 }}
                            value={customValue}
                            onChange={(val) => {
                                const numberValue = Number(val);
                                if (!isNaN(numberValue) && numberValue > 0) {
                                    handleCustomValueChange(numberValue);
                                }
                            }}
                            min={DEFAULT_CREDIT_AMOUNT}
                            step={DEFAULT_CREDIT_AMOUNT}
                            disabled={disabled}
                            allowDecimal={false}
                            clampBehavior="strict"
                        />
                        <Text size="sm" c="dimmed">
                            credits
                            {billingCycle === SubscriptionBillingCycle.YEARLY
                                ? ` × 12 months = ${IntlHelpers.formatNumber(customValue * multiplier)} `
                                : ''}
                        </Text>
                    </Group>
                </Card>
            )}

            {!!value && (
                <Card.Section
                    p="md"
                    mt="md"
                    bg="gray.0"
                    withBorder
                    style={{ borderColor: 'var(--mantine-color-gray-2)' }}
                >
                    <Text fw={600} fz="xs" mb={8}>
                        What {IntlHelpers.formatNumber(value * multiplier)} credits get you
                    </Text>
                    <Table withRowBorders withColumnBorders withTableBorder borderColor="gray.2" bg="white">
                        <Table.Tbody>
                            {CREDIT_SERVICES.flatMap((service) => {
                                const Icon = service.icon;
                                const totalCredits = value * multiplier;

                                if (service.creditsPerUse) {
                                    const quantity = Math.floor(totalCredits / service.creditsPerUse);
                                    return (
                                        <Table.Tr key={service.name}>
                                            <Table.Td>
                                                <Group gap={8} wrap="nowrap">
                                                    <Icon size={16} strokeWidth={1.25} style={{ flexShrink: 0 }} />
                                                    <Text size="sm" fw={600}>
                                                        {service.name}
                                                    </Text>
                                                </Group>
                                            </Table.Td>
                                            <Table.Td ta="right">
                                                <Text size="sm" fw={500}>
                                                    {IntlHelpers.formatNumber(quantity)}
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                }

                                if (service.sizing?.length) {
                                    const paidSizing = service.sizing.filter((s) => s.credits > 0);
                                    const rows = [];

                                    rows.push(
                                        <Table.Tr key={service.name}>
                                            <Table.Td colSpan={2} bg="gray.0">
                                                <Group gap={8} wrap="nowrap">
                                                    <Icon size={16} style={{ flexShrink: 0 }} />
                                                    <Text size="sm" fw={600}>
                                                        {service.name}
                                                    </Text>
                                                    <Text span size="sm" c="gray.5" fw={400} ml="auto">
                                                        (estimates)
                                                    </Text>
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>,
                                    );

                                    for (const size of paidSizing) {
                                        const count = Math.floor(totalCredits / size.credits);
                                        const unit = count === 1 ? size.unit : `${size.unit}s`;

                                        rows.push(
                                            <Table.Tr key={size.description}>
                                                <Table.Td>
                                                    <Text size="sm" ml={24}>
                                                        {size.description}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td ta="right">
                                                    {count > 0 ? (
                                                        <Text size="sm" fw={500}>
                                                            {count} {unit}{' '}
                                                        </Text>
                                                    ) : (
                                                        <Text size="sm">—</Text>
                                                    )}
                                                </Table.Td>
                                            </Table.Tr>,
                                        );
                                    }

                                    return rows;
                                }

                                return null;
                            })}
                        </Table.Tbody>
                    </Table>
                </Card.Section>
            )}
        </Card>
    );
};
